/** Display-currency helpers. Storage for unlabeled transport costs is RUB. */

export type CurrencyCode = string;

const SYMBOL_TO_CODE: Record<string, string> = {
  '₽': 'RUB',
  $: 'USD',
  '€': 'EUR',
  rub: 'RUB',
  usd: 'USD',
  eur: 'EUR',
};

const CODE_TO_SYMBOL: Record<string, string> = {
  RUB: '₽',
  USD: '$',
  EUR: '€',
};

/** Normalize settings symbol or ISO code → uppercase ISO. */
export function toCurrencyCode(raw: string | null | undefined, fallback = 'RUB'): string {
  const s = (raw || '').trim();
  if (!s) return fallback;
  const mapped = SYMBOL_TO_CODE[s] || SYMBOL_TO_CODE[s.toLowerCase()];
  if (mapped) return mapped;
  return s.toUpperCase().slice(0, 8);
}

export function toCurrencySymbol(codeOrSymbol: string | null | undefined): string {
  const code = toCurrencyCode(codeOrSymbol, 'RUB');
  return CODE_TO_SYMBOL[code] || code;
}

/**
 * Convert amount between currencies using RUB-based rates
 * (rates[X] = how many X per 1 RUB).
 */
export function convertAmount(
  amount: number,
  fromCode: string | null | undefined,
  toCode: string | null | undefined,
  rates: Record<string, number> | null | undefined,
): number {
  const n = Number(amount);
  if (!Number.isFinite(n)) return 0;
  const from = toCurrencyCode(fromCode, 'RUB');
  const to = toCurrencyCode(toCode, 'RUB');
  if (from === to) return n;
  if (!rates || Object.keys(rates).length === 0) return n;
  const fromRate = from === 'RUB' ? 1 : rates[from];
  const toRate = to === 'RUB' ? 1 : rates[to];
  if (!fromRate || !toRate || fromRate <= 0 || toRate <= 0) return n;
  const inRub = from === 'RUB' ? n : n / fromRate;
  return to === 'RUB' ? inRub : inRub * toRate;
}

export function roundMoney(amount: number): number {
  const n = Number(amount);
  if (!Number.isFinite(n)) return 0;
  if (Math.abs(n) >= 100) return Math.round(n);
  return Math.round(n * 100) / 100;
}
